import { Col, Row } from "antd";
import dayjs from "dayjs";
import { useFormik } from "formik";
import { defaultTo, get, isEmpty } from "lodash";
import moment from "moment";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { NAME_SPACES } from "shared/locales/constants";
import {
  CONTACT_POINT_SYSTEM,
  DATE_FORMAT,
  FAMILY_SITUATION,
  SITUATION_PROFESSIONAL,
  SOCIAL_REGIMES,
} from "utils/constants";
import { hundredYearsAgoStart } from "utils/helpers/date";
import { bindDateInputProps, bindInputProps } from "utils/helpers/input";
import { filterContractNumber } from "utils/helpers/validations";
import ActionButtons from "../Components/ActionButtons";
import Actions from "../Components/Actions";
import Header from "../Components/Header";
import Input from "../Components/Input";
import Switch from "../Components/Input/Switch";
import Layout from "../Components/Layout";
import Select from "../Components/Select";
import validation from "./validation";

const MUTUAL_MOBILITY_SOCIAL_REGIMES = {
  [SOCIAL_REGIMES.SECURITE_SOCIALE]: SOCIAL_REGIMES.SECURITE_SOCIALE,
  [SOCIAL_REGIMES.ALSACE_MOSELLE]: SOCIAL_REGIMES.ALSACE_MOSELLE,
};

const MUTUAL_MOBILITY_FAMILY_SITUATION = {
  [FAMILY_SITUATION.MARRIED]: FAMILY_SITUATION.MARRIED,
  [FAMILY_SITUATION.PACS]: FAMILY_SITUATION.PACS,
  [FAMILY_SITUATION.COHABITING]: FAMILY_SITUATION.COHABITING,
};

const MUTUAL_MOBILITY_SITUATION_PROFESSIONAL = {
  [SITUATION_PROFESSIONAL.SALARIED]: SITUATION_PROFESSIONAL.SALARIED,
  [SITUATION_PROFESSIONAL.STUDENT]: SITUATION_PROFESSIONAL.STUDENT,
  [SITUATION_PROFESSIONAL.RETIRED]: SITUATION_PROFESSIONAL.RETIRED,
  [SITUATION_PROFESSIONAL.SEARCHING_FOR_A_JOB]:
    SITUATION_PROFESSIONAL.SEARCHING_FOR_A_JOB,
};

function View({ initialValues, locked, onNext, deltas, onBack }) {
  const [hasContract, setHasContract] = useState(
    defaultTo(get(initialValues, "project.ria"), false),
  );
  const { t } = useTranslation();
  const { t: f } = useTranslation(NAME_SPACES.PUBLIC.PROCESSES);
  const TRANSLATION = f("B2C_MUTUAL_MOBILITY", {
    returnObjects: true,
  });

  const { handleSubmit, values, setFieldValue, ...formProps } = useFormik({
    initialValues,
    onSubmit: onNext,
    validationSchema: validation(
      t(`${NAME_SPACES.ERRORS}:VALIDATIONS`, { returnObjects: true }),
      {
        deltaRia: get(deltas, "start.ria"),
        deltaStart: get(deltas, "start.normal"),
        deltaEndRia: get(deltas, "end.ria"),
        deltaEnd: get(deltas, "end.normal"),
      },
    ),
    validateOnMount: true,
    validateOnChange: true,
    validateOnBlur: true,
  });

  return (
    <Layout>
      <Header
        title="Réponse immédiate ! Obtenez votre offre en moins de <b>2 minutes</b>"
        highlight="Réponse immédiate !"
        subtitle="Informations de l’adhérent"
        onBack={onBack}
      />
      <Row justify="center">
        <Col xs={24} md={16} lg={16}>
          <div className="grid--1">
            <div className="grid--2">
              <Input
                type="date"
                format={DATE_FORMAT}
                placeholder={"JJ / MM / AAAA"}
                label={"Date de naissance"}
                disabledDate={(current) =>
                  current > dayjs().subtract(18, "years").endOf("year") ||
                  current < hundredYearsAgoStart
                }
                value={
                  get(values, "project.contact.user.birthDate")
                    ? dayjs(get(values, "project.contact.user.birthDate"))
                    : undefined
                }
                defaultPickerValue={
                  !get(values, "project.contact.user.birthDate") &&
                  dayjs().subtract(30, "years")
                }
                onChange={(date) => {
                  const major = dayjs().subtract(18, "years").endOf("year");
                  if (date?.isValid() && date.isBefore(major)) {
                    setFieldValue(
                      "project.contact.user.birthDate",
                      date
                        .set("hour", 2)
                        .set("minute", 0)
                        .set("second", 0)
                        .toISOString(),
                    );
                  }
                }}
                onBlur={({ target: { value } }) => {
                  const major = dayjs().subtract(18, "years").endOf("year");
                  const date = dayjs(value, DATE_FORMAT);
                  if (date?.isValid() && date.isBefore(major))
                    setFieldValue(
                      "birthDate",
                      date ? date.add(2, "h").toISOString() : null,
                    );
                }}
              />
              <Input
                placeholder={"ex : 69001"}
                maxLength={5}
                label={"Code postal"}
                {...bindInputProps({
                  name: `telecoms.${CONTACT_POINT_SYSTEM.ZIP_CODE}.value`,
                  values,
                  ...formProps,
                })}
                onChange={({ target: { value } }) => {
                  setFieldValue(
                    `telecoms.${CONTACT_POINT_SYSTEM.CITY}.value`,
                    null,
                  );
                  setFieldValue(
                    `telecoms.${CONTACT_POINT_SYSTEM.ZIP_CODE}.value`,
                    value,
                  );
                }}
              />
            </div>
            <div className="grid--2">
              <Select
                options={Object.entries(MUTUAL_MOBILITY_FAMILY_SITUATION).map(
                  ([key, value]) => ({
                    value,
                    label: TRANSLATION.FORM.FAMILY_SITUATION.OPTIONS[key],
                  }),
                )}
                label={TRANSLATION.FORM.FAMILY_SITUATION.LABEL}
                placeholder={TRANSLATION.FORM.FAMILY_SITUATION.PLACEHOLDER}
                {...bindInputProps({
                  name: "project.contact.fields.familySituation",
                  values,
                  ...formProps,
                })}
                disabled={locked}
              />
              <Select
                options={Object.entries(
                  MUTUAL_MOBILITY_SITUATION_PROFESSIONAL,
                ).map(([key, value]) => ({
                  value,
                  label: TRANSLATION.FORM.SITUATION_PROFESSIONAL.OPTIONS[key],
                }))}
                label={TRANSLATION.FORM.SITUATION_PROFESSIONAL.LABEL}
                placeholder={
                  TRANSLATION.FORM.SITUATION_PROFESSIONAL.PLACEHOLDER
                }
                {...bindInputProps({
                  name: "project.contact.socioProfessionalCategory",
                  values,
                  ...formProps,
                })}
                disabled={locked}
              />
            </div>
            <div className="grid--2">
              <Select
                options={Object.entries(MUTUAL_MOBILITY_SOCIAL_REGIMES).map(
                  ([key, value]) => ({
                    value,
                    label: TRANSLATION.FORM.MANDATORY_SCHEME.OPTIONS[key],
                  }),
                )}
                label={TRANSLATION.FORM.MANDATORY_SCHEME.LABEL}
                placeholder={TRANSLATION.FORM.MANDATORY_SCHEME.PLACEHOLDER}
                {...bindInputProps({
                  name: "project.contact.socialRegime",
                  values,
                  ...formProps,
                })}
                disabled={locked}
              />
              <Input
                type="date"
                format={DATE_FORMAT}
                placeholder={"JJ / MM / AAAA"}
                label={"Date d’effet"}
                disabledDate={(current) => current < moment()}
                {...bindDateInputProps({
                  name: "project.contract.issuanceDate",
                  setFieldValue,
                  values,
                  ...formProps,
                })}
              />
            </div>
            <div className="form-field">
              <Input
                type="checkbox"
                label={
                  "J’autorise Mobilité <span>Mutuelle</span> à collecter et utiliser les données personnelles nécessaires à l’exécution de mon devis et une proposition adaptée à mes besoins."
                }
                checked={get(values, "project.contact.acceptContact") || false}
                {...bindInputProps({
                  name: "project.contact.acceptContact",
                  values,
                  ...formProps,
                })}
                touched
              />
            </div>
            <div className="form-field">
              <Input
                type="checkbox"
                label={
                  "Je dispose d’un contrat santé auprès d’un autre organisme assureur."
                }
                checked={hasContract}
                onChange={({ target: { checked } }) => {
                  setHasContract(checked);
                  setFieldValue("project.ria", checked);
                  setFieldValue("project.terminationRequest", null);
                  if (checked)
                    setFieldValue("project.fields.selfOccupation", false);
                  else
                    setFieldValue("project.fields.selfOccupation", undefined);
                }}
              />
              {hasContract && (
                <div className="termination-container">
                  <div className="termination-container__question">
                    <Actions
                      variant={"normal"}
                      label={TRANSLATION.FORM.RIA.OLD_CONTRACT_LABEL}
                      actions={
                        <Switch
                          className="button__switch"
                          checked={get(values, "project.ria")}
                          checkedChildren={" "}
                          unCheckedChildren={" "}
                          onChange={(checked) => {
                            setFieldValue("project.ria", checked);
                            if (checked)
                              setFieldValue(
                                "project.fields.selfOccupation",
                                false,
                              );
                            else {
                              setFieldValue(
                                "project.fields.selfOccupation",
                                undefined,
                              );
                            }
                          }}
                        />
                      }
                    />
                    {get(values, "project.ria") && (
                      <>
                        <hr />
                        <span className="termination-container__question--title">
                          Comment souhaitez-vous gérer la résiliation de votre
                          ancien contrat ?
                        </span>
                        <Input
                          type="checkbox"
                          label={
                            "Je souhaite que <b>Mobilité Mutuelle effectue pour votre compte, la résiliation</b> de votre contrat actuellement en vigueur auprès de votre assureur."
                          }
                          checked={
                            get(values, "project.fields.selfOccupation") ===
                            false
                          }
                          onChange={() =>
                            setFieldValue(
                              "project.fields.selfOccupation",
                              false,
                            )
                          }
                        />
                        {get(values, "project.fields.selfOccupation") ===
                          false && (
                          <div className="termination-container__question--block">
                            <Input
                              label={
                                TRANSLATION.FORM.CURRENT_EFFECTIVE_DATE.LABEL
                              }
                              placeholder={
                                TRANSLATION.FORM.CURRENT_EFFECTIVE_DATE
                                  .PLACEHOLDER
                              }
                              type="date"
                              variant="simple"
                              format={DATE_FORMAT}
                              defaultPickerValue={dayjs().add(-1, "day")}
                              disabledDate={(current) => {
                                return current && current > dayjs();
                              }}
                              {...bindDateInputProps({
                                name: `project.terminationRequest.dueDate`,
                                values,
                                setFieldValue,
                                ...formProps,
                              })}
                              disabled={locked}
                            />
                            <Input
                              label={TRANSLATION.FORM.CURRENT_CONTRACT.LABEL}
                              placeholder={
                                TRANSLATION.FORM.CURRENT_CONTRACT.PLACEHOLDER
                              }
                              {...bindInputProps({
                                name: `project.terminationRequest.contractNumber`,
                                values,
                                setFieldValue,
                                ...formProps,
                              })}
                              onChange={({ target: { value } }) => {
                                const filteredValue =
                                  filterContractNumber(value);
                                setFieldValue(
                                  `project.terminationRequest.contractNumber`,
                                  filteredValue,
                                );
                              }}
                              onBlur={({ target: { value } }) => {
                                const trimmedValue = value.trim();
                                setFieldValue(
                                  `project.terminationRequest.contractNumber`,
                                  trimmedValue,
                                );
                                formProps.setFieldTouched(
                                  `project.terminationRequest.contractNumber`,
                                  true,
                                );
                              }}
                              disabled={locked}
                            />
                          </div>
                        )}
                        <Input
                          type="checkbox"
                          label={"Je me charge de résilier moi même"}
                          checked={
                            get(values, "project.fields.selfOccupation") ===
                            true
                          }
                          onChange={() =>
                            setFieldValue("project.fields.selfOccupation", true)
                          }
                        />
                      </>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </Col>
      </Row>
      <ActionButtons
        disabled={!isEmpty(formProps?.errors)}
        onNext={handleSubmit}
      />
    </Layout>
  );
}

export default View;
